<script lang="ts">
	// The passcode entry, shared by the lock screen and the lock over a till.
	//
	// Where the till is and what it is waiting on at the inline start, and
	// at the inline end one of two parts, never both: the shop's names to
	// pick from, or the picked name with its dots and pad. A cashier standing
	// at one of several tills can see which one they are about to sell from
	// before they touch anything. Below 900 px the two stack.
	//
	// The names are read each time this appears, and again on "Not you?", so
	// a cashier the owner has just added is there without a reload.
	//
	// A sale that may already exist is urgent; a cart left open is only worth
	// mentioning. A server that cannot be reached is said in words with a way
	// to try again, rather than as a wrong passcode.

	import Band from '../components/Band.svelte';
	import BrandMark from '../components/BrandMark.svelte';
	import Button from '../components/Button.svelte';
	import Icon from '../components/Icon.svelte';
	import Keypad from '../components/Keypad.svelte';
	import NameTile from '../components/NameTile.svelte';
	import PasscodeDots from '../components/PasscodeDots.svelte';
	import { message, type Language } from '../i18n/messages';
	import { formatMinor } from '../format/money';
	import { passcodeMessage, type PasscodeEntry } from '../till/passcode.svelte';
	import { Roster } from '../till/roster.svelte';
	import type { StoredSummary } from '../draft/draft.svelte';
	import { untrack } from 'svelte';

	interface Props {
		language: Language;
		/** Account, shop and till, joined. */
		where: string;
		/** The heading while a name is to be picked. */
		whoTitle: string;
		/** The heading once a name is picked and the passcode is typed. */
		title: string;
		entry: PasscodeEntry;
		waiting?: StoredSummary | null;
		minorDigits: number;
		whereTestId: string;
		dotsTestId: string;
		messageTestId: string;
		padTestId: string;
		/** Prefixes the names, the picked name and "Not you?". */
		pickTestId: string;
	}

	let {
		language,
		where,
		whoTitle,
		title,
		entry,
		waiting = null,
		minorDigits,
		whereTestId,
		dotsTestId,
		messageTestId,
		padTestId,
		pickTestId,
	}: Props = $props();

	const roster = new Roster();
	$effect(() => {
		untrack(() => void roster.load());
	});

	const passcodeUnreachable = $derived(entry.phase === 'error' && entry.failure === 'unreachable');
	// A list that failed to load matters only while a name is to be picked: a
	// cashier already picked over the till can still type.
	const unreachable = $derived(
		passcodeUnreachable || (entry.picked === null && roster.status === 'failed'),
	);

	function retry() {
		if (roster.status === 'failed') void roster.load();
		if (passcodeUnreachable) entry.backspace();
	}

	function notYou() {
		entry.unpick();
		void roster.load();
	}
</script>

<div class="lockpad">
	<div class="about">
		<BrandMark size={26} />
		<div class="heading">
			<span class="where" data-testid={whereTestId}>{where}</span>
			<h1>{entry.picked ? title : whoTitle}</h1>
		</div>
		{#if waiting?.unsent}
			<Band
				tone="flag"
				badge={message(language, 'lockUrgent')}
				badgeSize="md"
				testId="lock-waiting"
			>
				{message(language, 'lockUnsentSale')}:
				<bdi class="n" dir="ltr">{waiting.lineCount}</bdi>
				{message(language, 'lockItems')},
				<bdi class="n" dir="ltr">{formatMinor(waiting.totalMinor, minorDigits)}</bdi>
			</Band>
		{:else if waiting}
			<div class="draft" data-testid="lock-waiting">
				<Icon name="bag" size={20} />
				<span>
					{message(language, 'lockOrderWaiting')}:
					<bdi class="n" dir="ltr">{waiting.lineCount}</bdi>
					{message(language, 'lockItems')},
					<bdi class="n" dir="ltr">{formatMinor(waiting.totalMinor, minorDigits)}</bdi>
				</span>
			</div>
		{/if}
		{#if unreachable}
			<Band
				tone="flag"
				testId="lock-unreachable"
				action={message(language, 'doneRetry')}
				actionTestId="lock-retry"
				onAction={retry}
			>
				{message(language, 'lockUnreachable')}
			</Band>
		{/if}
	</div>
	{#if entry.picked}
		<div class="entry">
			<div class="who" data-testid="{pickTestId}-picked">
				<Icon name="user" size={20} />
				<span class="picked">{entry.picked.name}</span>
				<Button
					testId="{pickTestId}-not-you"
					tone="text"
					size="sm"
					label={message(language, 'lockNotYou')}
					disabled={entry.phase === 'checking'}
					onPress={notYou}
				/>
			</div>
			<PasscodeDots
				filled={entry.filled}
				state={entry.dots}
				message={passcodeUnreachable ? '' : passcodeMessage(entry, language)}
				label={message(language, 'lockPasscode')}
				{dotsTestId}
				{messageTestId}
			/>
			<Keypad
				testId={padTestId}
				label={message(language, 'lockPasscode')}
				deleteLabel={message(language, 'backspace')}
				disabled={!entry.open}
				canDelete={entry.canDelete}
				onKey={(key) => entry.type(key)}
				onBackspace={() => entry.backspace()}
			/>
		</div>
	{:else}
		<div class="pick">
			{#if roster.status === 'ready' && roster.cashiers.length === 0}
				<div class="nobody" data-testid="{pickTestId}-nobody">
					<p class="nobody-title">{message(language, 'lockNobody')}</p>
					<p class="nobody-hint">{message(language, 'lockNobodyHint')}</p>
				</div>
			{/if}
			<ul
				class="names"
				data-scroll=""
				role="list"
				aria-label={message(language, 'lockNames')}
				data-testid="{pickTestId}-names"
			>
				{#each roster.cashiers as cashier (cashier.id)}
					<NameTile
						testId="{pickTestId}-name"
						name={cashier.name}
						onPick={() => entry.pick(cashier)}
					/>
				{/each}
			</ul>
		</div>
	{/if}
</div>

<style>
	.lockpad {
		flex: 1;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: var(--space-12);
		padding: var(--space-9);
		background: var(--surface-page);
		flex-wrap: wrap;
	}

	.about {
		flex: 1 1 360px;
		min-inline-size: 0;
		max-inline-size: 420px;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.heading {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.where {
		font-size: var(--text-sm);
		color: var(--text-muted);
	}

	h1 {
		margin: 0;
		font-size: var(--text-h3);
		font-weight: 700;
		letter-spacing: var(--tracking-tight);
		line-height: var(--leading-headline);
	}

	.draft {
		display: flex;
		align-items: center;
		gap: var(--space-4);
		padding: var(--space-5) var(--space-7);
		background: var(--surface-card);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-card);
		font-size: var(--text-sm);
	}

	.n {
		font-family: var(--font-mono);
	}

	.entry {
		flex: none;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: var(--space-5);
	}

	.who {
		display: flex;
		align-items: center;
		gap: var(--space-3);
		font-size: var(--text-base);
	}

	.picked {
		font-weight: 700;
		color: var(--text-strong);
	}

	.pick {
		flex: 1 1 440px;
		min-inline-size: 0;
		max-inline-size: 600px;
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
	}

	.nobody {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
		padding: var(--space-7);
		background: var(--surface-card);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-card);
	}

	.nobody-title {
		margin: 0;
		font-size: var(--text-base);
		font-weight: 700;
		color: var(--text-strong);
	}

	.nobody-hint {
		margin: 0;
		font-size: var(--text-sm);
		color: var(--text-body);
	}

	/* 22 names fit one view at 1280x800; a longer list scrolls inside the
	 * column rather than pushing the page. */
	/* Only the list's own start and block padding go: the end is the gap
	 * before the scrollbar, from data-scroll. */
	.names {
		margin: 0;
		padding-block: 0;
		padding-inline-start: 0;
		list-style: none;
		max-block-size: min(600px, 72vh);
		overflow-y: auto;
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(var(--name-min), 1fr));
		gap: var(--touch-gap);
	}
</style>
